import type { Auth0Client, LoginOpts } from './types/auth0Client';
import { clearCoralTokenCache } from './coral';
import type { LoginAppState } from './types';
import { trackUserLoginEvent } from './reporting';
import type { LoginReason } from '@tv2no/tv2no-snowplow-tracking';
import { isLoginReason } from './utils';

export const setupClient = (): Promise<Auth0Client> => {
  const { callbackUrl, domain, clientId, userApiUrl } = window.AUTH0_CONFIG;

  if (!window.auth0) return Promise.reject('auth0 script not loaded');

  const buildRedirectUri = (internalUrl: string) => 
    `${callbackUrl}?internalUrl=${encodeURIComponent(internalUrl)}`;

  return window.auth0
    .createAuth0Client({
      domain,
      clientId,
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
      authorizationParams: {
        audience: `https://${domain}`,
        redirect_uri: buildRedirectUri(location.href.toString()),
      },
    })
    .then(auth0Client => {
      const login = async function (arg1?: string | LoginReason, arg2?: string, arg3?: string) {
        const redirectUrl = arg3 ? buildRedirectUri(arg3) : undefined;
        
        if (isLoginReason(arg1)) {
          const reason = arg1;
          const appStatePayload: LoginAppState = { ...reason };
          const opts: LoginOpts = {
            appState: appStatePayload,
            authorizationParams: {
              ...(redirectUrl && { redirect_uri: redirectUrl })
            },
          };

          await auth0Client.loginWithRedirect(opts);
        } else {
          const opts: LoginOpts = {
            authorizationParams: {},
          };
          const reasonToLoginCategory = typeof arg1 === 'string' ? arg1 : undefined;
          const reasonToLoginName = arg2;
          if (reasonToLoginCategory) {
            opts.authorizationParams.reasonToLoginCategory = reasonToLoginCategory;
          }
          if (reasonToLoginName) {
            opts.authorizationParams.reasonToLoginName = reasonToLoginName;
          }
          if (redirectUrl) {
            opts.authorizationParams.redirect_uri = redirectUrl;
          }

          await auth0Client.loginWithRedirect(opts);
        }
      };

      const logout = async function () {
        const user = await window?.TV2?.getLoggedInUser();
        const userId = user?.id;

        trackUserLoginEvent('Logout Clicked', userId);

        clearCoralTokenCache();

        await auth0Client.logout({
          logoutParams: {
            returnTo: `${callbackUrl}?internalUrl=${encodeURIComponent(location.href.toString())}&logout=true`,
          },
        });
      };

      const isAuthenticated = async function () {
        return await auth0Client.isAuthenticated();
      };

      const getLoggedInUser = async function () {
        try {
          const token = await auth0Client.getTokenSilently({ authorizationParams: { redirect_uri: location.origin } });

          if (!token) return;

          const userResponse = await fetch(userApiUrl, {
            method: 'get',
            credentials: 'include',
            headers: new Headers({
              Authorization: 'Bearer ' + token,
            }),
          });

          const userData = await userResponse.json();

          return userData;
        } catch (e) {
          return;
        }
      };

      const getToken = async function () {
        return await auth0Client.getTokenSilently({ authorizationParams: { redirect_uri: location.origin } });
      };

      const verifyEmail = async function () {
        try {
          const token = await window.TV2?.getToken();

          const response = await fetch(`${userApiUrl}verifyEmail`, {
            method: 'POST',
            headers: new Headers({
              Authorization: 'Bearer ' + token
            }),
          });

          if (!response.ok || response.status !== 204) {
            throw new Error(`User verification email failed: ${response.status}`);
          }

          const user = await window.TV2.getLoggedInUser();

          return {
            success: true,
            message: 'Verification email sent',
            email: user?.email
          }
        } catch (e) {
          console.error('Menu: Error verifying email:', e);

          return {
            success: false,
            message: (e as Error).message
          }
        }
      };

      window.TV2 = { ...window.TV2, login, logout, isAuthenticated, getLoggedInUser, getToken, verifyEmail };

      return auth0Client;
    });
};
